'use client';

import * as React from 'react';

import { cn } from '@v2/utils';

import {
  applyThumbMove,
  getClosestThumbIndex,
  valueFromTrackPointer,
  type DualRangeValue,
} from './range-slider-math';

type ThumbIndex = 0 | 1;

type DualRangeSliderProps = {
  min: number;
  max: number;
  value: DualRangeValue;
  onValueChange: (value: DualRangeValue) => void;
  onValueCommit?: (value: DualRangeValue) => void;
  step?: number;
  minGap?: number;
  disabled?: boolean;
  className?: string;
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
  minThumbAriaLabel?: string;
  maxThumbAriaLabel?: string;
  formatAriaValue?: (value: number) => string;
  renderThumb?: (args: { index: ThumbIndex; value: number }) => React.ReactNode;
};

function asTuple(value: DualRangeValue): DualRangeValue {
  return [value[0], value[1]];
}

export const DualRangeSlider = React.forwardRef<
  HTMLDivElement,
  DualRangeSliderProps
>(function DualRangeSlider(
  {
    min,
    max,
    value,
    onValueChange,
    onValueCommit,
    step = 1,
    minGap = step,
    disabled = false,
    className,
    trackClassName,
    rangeClassName,
    thumbClassName,
    minThumbAriaLabel = 'Minimum',
    maxThumbAriaLabel = 'Maximum',
    formatAriaValue,
    renderThumb,
  },
  ref
) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const valueRef = React.useRef(value);
  const dragRef = React.useRef<ThumbIndex | null>(null);
  const onValueChangeRef = React.useRef(onValueChange);
  const onValueCommitRef = React.useRef(onValueCommit);
  const boundsRef = React.useRef({ min, max, step, minGap });

  valueRef.current = value;
  onValueChangeRef.current = onValueChange;
  onValueCommitRef.current = onValueCommit;
  boundsRef.current = { min, max, step, minGap };

  const span = Math.max(max - min, step);
  const minPercent = Math.min(
    100,
    Math.max(0, ((value[0] - min) / span) * 100)
  );
  const maxPercent = Math.min(
    100,
    Math.max(0, ((value[1] - min) / span) * 100)
  );

  const readValueFromPointer = React.useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return boundsRef.current.min;
    const rect = track.getBoundingClientRect();
    return valueFromTrackPointer({
      clientX,
      trackLeft: rect.left,
      trackWidth: rect.width,
      min: boundsRef.current.min,
      max: boundsRef.current.max,
    });
  }, []);

  const moveThumb = React.useCallback(
    (thumb: ThumbIndex, clientX: number) => {
      const {
        min: boundMin,
        max: boundMax,
        step: boundStep,
        minGap: gap,
      } = boundsRef.current;
      const next = applyThumbMove({
        current: asTuple(valueRef.current),
        thumb,
        nextValue: readValueFromPointer(clientX),
        min: boundMin,
        max: boundMax,
        step: boundStep,
        minGap: gap,
      });
      if (next[0] === valueRef.current[0] && next[1] === valueRef.current[1]) {
        return next;
      }
      onValueChangeRef.current(next);
      return next;
    },
    [readValueFromPointer]
  );

  const stopDragging = React.useCallback(() => {
    if (dragRef.current === null) return;
    dragRef.current = null;
    onValueCommitRef.current?.(asTuple(valueRef.current));
  }, []);

  React.useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (dragRef.current === null) return;
      event.preventDefault();
      event.stopPropagation();
      moveThumb(dragRef.current, event.clientX);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (dragRef.current === null) return;
      event.preventDefault();
      event.stopPropagation();
      stopDragging();
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp, true);
    window.addEventListener('pointercancel', onPointerUp, true);

    return () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp, true);
      window.removeEventListener('pointercancel', onPointerUp, true);
    };
  }, [moveThumb, stopDragging]);

  const startDragging = (thumb: ThumbIndex, clientX: number) => {
    if (disabled) return;
    dragRef.current = thumb;
    moveThumb(thumb, clientX);
  };

  const onTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerValue = readValueFromPointer(event.clientX);
    const thumb = getClosestThumbIndex(asTuple(valueRef.current), pointerValue);
    startDragging(thumb, event.clientX);
  };

  const onThumbPointerDown =
    (thumb: ThumbIndex) => (event: React.PointerEvent<HTMLSpanElement>) => {
      if (disabled || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.focus();
      startDragging(thumb, event.clientX);
    };

  const onThumbKeyDown =
    (thumb: ThumbIndex) => (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return;
      const {
        min: boundMin,
        max: boundMax,
        step: boundStep,
        minGap: gap,
      } = boundsRef.current;
      const current = asTuple(valueRef.current);
      const direction =
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowDown' ||
        event.key === 'PageDown'
          ? -1
          : event.key === 'ArrowRight' ||
              event.key === 'ArrowUp' ||
              event.key === 'PageUp'
            ? 1
            : 0;
      const multiplier =
        event.key === 'PageUp' || event.key === 'PageDown' || event.shiftKey
          ? 10
          : 1;

      let nextValue = current[thumb];
      if (direction !== 0) {
        nextValue = current[thumb] + direction * boundStep * multiplier;
      } else if (event.key === 'Home') {
        nextValue = thumb === 0 ? boundMin : current[0] + gap;
      } else if (event.key === 'End') {
        nextValue = thumb === 1 ? boundMax : current[1] - gap;
      } else {
        return;
      }

      event.preventDefault();
      const next = applyThumbMove({
        current,
        thumb,
        nextValue,
        min: boundMin,
        max: boundMax,
        step: boundStep,
        minGap: gap,
      });
      onValueChange(next);
      onValueCommit?.(next);
    };

  const thumbLabels = [minThumbAriaLabel, maxThumbAriaLabel] as const;

  return (
    <div
      ref={ref}
      data-vaul-no-drag=""
      className={cn(
        'relative flex h-6 w-full touch-none select-none items-center',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
    >
      <div
        ref={trackRef}
        data-testid="dual-range-slider-track"
        className={cn(
          'relative h-2 w-full rounded-full bg-gray-300',
          trackClassName
        )}
        onPointerDown={onTrackPointerDown}
      >
        <div
          className={cn(
            'absolute h-full rounded-full bg-gray-800',
            rangeClassName
          )}
          style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
        />
      </div>
      {([0, 1] as const).map((index) => (
        <span
          key={index}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={thumbLabels[index]}
          aria-valuemin={index === 0 ? min : value[0]}
          aria-valuemax={index === 0 ? value[1] : max}
          aria-valuenow={value[index]}
          aria-valuetext={
            formatAriaValue
              ? formatAriaValue(value[index])
              : String(value[index])
          }
          aria-disabled={disabled || undefined}
          data-thumb={index === 0 ? 'min' : 'max'}
          className={cn(
            'absolute top-1/2 block h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gray-800 bg-white shadow-sm',
            "before:absolute before:-inset-3 before:content-['']",
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            index === 0 ? 'z-[1]' : 'z-[2]',
            thumbClassName
          )}
          style={{ left: `${index === 0 ? minPercent : maxPercent}%` }}
          onPointerDown={onThumbPointerDown(index)}
          onKeyDown={onThumbKeyDown(index)}
        >
          {renderThumb?.({ index, value: value[index] })}
        </span>
      ))}
    </div>
  );
});
